import { common } from '@mui/material/colors';
import { svSE } from '@mui/material/locale';
import {
  createTheme as createMuiTheme,
  Theme,
  ThemeOptions,
} from '@mui/material/styles';
import { svSE as dateSvSE } from '@mui/x-date-pickers/locales';
import { hexToRgba } from '../utils/color';
import { pxToRem } from '../utils/text';

declare module '@mui/material/styles' {
  interface Palette {
    accent: Palette['primary'];
    decoration: {
      energy: string;
    };
    chart: string[];
  }
  interface PaletteOptions {
    accent: PaletteOptions['primary'];
    decoration?: {
      energy: string;
    };
    chart?: string[];
  }
  interface TypeAction {
    pressed: string;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    preamble: React.CSSProperties;
    badgeLabel?: React.CSSProperties;
    alertTitle?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
  }

  // allow configuration using `createTheme()`
  export interface TypographyVariantsOptions {
    preamble?: React.CSSProperties;
    badgeLabel?: React.CSSProperties;
    alertTitle?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    preamble: true;
    badgeLabel: true;
    alertTitle: true;
    subtitle3: true;
  }
}

export type PaletteColors =
  'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'accent';

const resolvePalette = (theme: {
  palette: Theme['palette'];
  vars?: Theme['vars'];
}): Theme['palette'] =>
  ((theme.vars ?? theme) as { palette: Theme['palette'] }).palette;

export const buttonRadius = '24px';
export const listBoxRadius = '4px';

// Action opacity constants - används både i palette och button styling
const hoverOpacity = 0.08;
const selectedOpacity = 0.12;
const pressedOpacity = 0.16;
const focusOpacity = pressedOpacity;

const malarenergiSansFamily = 'Malarenergi Sans, sans-serif';
const openSansFamily = 'Open Sans, sans-serif';

const getThemeTokens = (darkMode: boolean) => {
  const primaryColors = darkMode
    ? {
        light: '#082647',
        main: '#7A98BF',
        dark: '#0C3666', //#002850
        contrastText: '#121212',
      }
    : {
        light: '#E5EEFB',
        main: '#244A75', // main color for light mode #3D5E85
        dark: '#002850',
        contrastText: '#FFFFFF',
      };
  const secondaryColors = darkMode
    ? {
        light: '#082647',
        main: '#6699E7',
        dark: '#4C88E3',
      }
    : {
        light: '#CCDDF7',
        main: '#0055D7',
        dark: '#0044AC',
      };
  const textPrimary = darkMode ? '#FFFFFF' : '#121212';
  const textSecondary = darkMode
    ? 'rgba(255, 255, 255, 0.7)'
    : 'rgba(18, 18, 18, 0.7)';
  const background = darkMode
    ? { default: '#061B33', paper: '#0A2B52' }
    : { default: '#FFFFFF', paper: '#F5F0EB' };

  // Constants for success, error, info, and warning
  const successColors = darkMode
    ? {
        light: '#0B2F1A',
        main: '#77AD8D',
        dark: '#338454',
      }
    : {
        light: '#D2E4D9',
        main: '#196A3A',
        dark: '#165E34',
      };

  const errorColors = darkMode
    ? {
        light: '#4F1010',
        main: '#DD7E7E',
        dark: '#D15353',
      }
    : {
        light: '#F4D4D4',
        main: '#B22424',
        dark: '#9E2020',
      };

  const infoColors = secondaryColors;

  const warningColors = darkMode
    ? {
        light: '#4D4121',
        main: '#FFE59A',
        dark: '#CCAE59',
      }
    : {
        light: '#FFF4D4',
        main: '#FFDA6F',
        dark: '#998343',
      };

  const dividerColors = darkMode
    ? hexToRgba('#FFFFFF', 0.12)
    : hexToRgba('#121212', 0.12);

  const action = {
    active: darkMode ? '#FFFFFF8F' : '#0000008A',
    pressed: darkMode ? '#ffffff29' : '#00000029',
    hover: darkMode ? '#ffffff14' : '#00000014',
    selected: darkMode ? '#ffffff1f' : '#0000001f',
  };

  const chartColors = darkMode
    ? [
        '#7A9DD4',
        '#C19372',
        '#907BCB',
        '#7B9A88',
        '#CE7484',
        '#49AAB9',
        '#D3AD55',
        '#B76FBB',
        '#79A253',
        '#C85560',
      ]
    : [
        '#5074AB',
        '#AD6D19',
        '#7B69AE',
        '#587856',
        '#B96E99',
        '#3E919D',
        '#B08414',
        '#A14C9A',
        '#2F537C',
        '#B44D56',
      ];

  return {
    primaryColors,
    secondaryColors,
    textPrimary,
    textSecondary,
    background,
    successColors,
    errorColors,
    warningColors,
    infoColors,
    dividerColors,
    action,
    chartColors,
  };
};

type ThemeTokens = ReturnType<typeof getThemeTokens>;

const buildPalette = (tokens: ThemeTokens) => ({
  background: tokens.background,
  primary: tokens.primaryColors,
  secondary: tokens.secondaryColors,
  accent: {
    main: '#FF6932',
  },
  decoration: {
    energy: '#FF6932',
  },
  success: tokens.successColors,
  warning: tokens.warningColors,
  error: tokens.errorColors,
  info: tokens.infoColors,
  text: {
    primary: tokens.textPrimary,
    secondary: tokens.textSecondary,
  },
  action: {
    active: tokens.action.active,
    pressed: tokens.action.pressed,
    hover: tokens.action.hover,
    selected: tokens.action.selected,
    hoverOpacity,
    selectedOpacity,
    focusOpacity,
  },
  divider: tokens.dividerColors,
  chart: tokens.chartColors,
});

export const createTheme = (
  darkMode = false,
  baseUrl = '',
  cssVariables = false,
): Theme => {
  const defaultTheme = createMuiTheme();
  const lightTokens = getThemeTokens(false);
  const darkTokens = getThemeTokens(true);
  const tokens = darkMode ? darkTokens : lightTokens;
  const { textPrimary, textSecondary } = tokens;

  const typographyTextPrimary = cssVariables
    ? 'var(--mui-palette-text-primary)'
    : textPrimary;
  const typographyTextSecondary = cssVariables
    ? 'var(--mui-palette-text-secondary)'
    : textSecondary;
  const focusOutlineColor = cssVariables
    ? 'var(--mui-palette-text-primary)'
    : darkMode
      ? common.white
      : common.black;
  const snackbarFocusOutlineColor = cssVariables
    ? 'var(--mui-palette-background-default)'
    : !darkMode
      ? common.white
      : common.black;

  const paletteOptions: ThemeOptions = cssVariables
    ? {
        cssVariables: { colorSchemeSelector: 'media' },
        colorSchemes: {
          light: { palette: buildPalette(lightTokens) },
          dark: { palette: buildPalette(darkTokens) },
        },
      }
    : {
        palette: {
          mode: darkMode ? ('dark' as const) : ('light' as const),
          ...buildPalette(tokens),
        },
      };

  const theme = createMuiTheme(
    {
      ...paletteOptions,
      typography: {
        fontFamily:
          'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
        h1: {
          fontSize: pxToRem(36),
          fontWeight: 800,
          fontFamily: malarenergiSansFamily,
          [defaultTheme.breakpoints.up('md')]: {
            fontSize: pxToRem(54),
          },
        },
        h2: {
          fontSize: pxToRem(28),
          fontWeight: 700,
          fontFamily: malarenergiSansFamily,
          lineHeight: pxToRem(38),
          letterSpacing: 0,
        },
        h3: {
          fontSize: pxToRem(22),
          fontWeight: 700,
          fontFamily: openSansFamily,
          lineHeight: pxToRem(32),
          letterSpacing: 0,
        },
        h4: {
          fontSize: pxToRem(20),
          fontWeight: 700,
          fontFamily: openSansFamily,
          lineHeight: pxToRem(30),
          letterSpacing: 0,
        },
        h5: {
          fontSize: pxToRem(20),
          fontWeight: 600,
          fontFamily: openSansFamily,
          lineHeight: pxToRem(30),
          letterSpacing: 0,
          color: typographyTextPrimary,
        },
        h6: {
          fontSize: pxToRem(18),
          fontWeight: 700,
          color: typographyTextSecondary,
        },
        body1: {
          fontSize: pxToRem(16),
        },
        body2: {
          fontSize: pxToRem(14),
        },
        subtitle1: {
          fontWeight: 600,
          letterSpacing: 0,
        },
        subtitle2: {
          fontWeight: 600,
        },
        subtitle3: {
          fontWeight: 600,
          fontSize: pxToRem(12),
          lineHeight: pxToRem(12),
          letterSpacing: 0,
        },
        preamble: {
          fontSize: pxToRem(18),
          fontWeight: 400,
          color: typographyTextSecondary,
        },
        badgeLabel: {
          fontSize: pxToRem(12),
          fontWeight: 600,
          letterSpacing: pxToRem(0),
          lineHeight: pxToRem(20),
        },
        alertTitle: {
          fontSize: pxToRem(16),
          fontWeight: 600,
          letterSpacing: 0,
          lineHeight: '150%',
          color: typographyTextPrimary,
        },
        overline: {
          letterSpacing: '1px',
        },
      },
    },
    svSE,
    dateSvSE,
  );

  const interactionStates = {
    hover: {
      '&:hover': {
        boxShadow: `inset 0 0 0 999px ${(theme.vars ?? theme).palette.action.hover}`,
      },
    },
    pressed: {
      '&:active': {
        boxShadow: `inset 0 0 0 999px ${(theme.vars ?? theme).palette.action.pressed}`,
      },
    },
    focus: {
      '&:focus-visible': {
        outline: `2px dashed ${focusOutlineColor}`,
        outlineOffset: '2px',
      },
    },
  };

  theme.components = {
    ...(theme.components || {}),
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          '&:not(.MuiBackdrop-invisible)': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAccordion: {
      defaultProps: {
        square: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          background: 'transparent',
          boxShadow: 'none',
          borderBottom: `1px solid ${resolvePalette(theme).action.active}`,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
            padding: 0,
            background: resolvePalette(theme).background.paper,
          },
        }),
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: '48px',
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.hover,
          ...interactionStates.pressed,
          '&.Mui-expanded': {
            minHeight: '48px',
          },
        },
        content: {
          margin: '16px 0',
          '&.Mui-expanded': {
            margin: '16px 0',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.hover,
          ...interactionStates.pressed,
          '& .MuiListItemText-primary': {
            fontSize: pxToRem(16),
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'all 0.1s ease-in-out',
          '&:hover': {
            ...interactionStates.hover['&:hover'],
            backgroundColor: 'transparent',
          },
          '&:active': {
            ...interactionStates.pressed['&:active'],
            backgroundColor: 'transparent',
          },
          color: resolvePalette(theme).text.secondary,
          '& .MuiListItemText-primary': {
            ...theme.typography.body1,
            color: resolvePalette(theme).text.secondary,
          },
          '& .MuiListItemText-secondary': {
            ...theme.typography.body2,
            color: resolvePalette(theme).text.secondary,
          },
          '& .MuiListItemIcon-root': {
            color: resolvePalette(theme).action.active,
          },
          '&.Mui-selected': {
            ...theme.typography.subtitle1,
            backgroundColor: resolvePalette(theme).action.selected,
            color: resolvePalette(theme).text.primary,
            '& .MuiListItemText-primary': {
              ...theme.typography.subtitle1,
              color: resolvePalette(theme).text.primary,
            },
            '& .MuiListItemText-secondary': {
              ...theme.typography.body2,
              color: resolvePalette(theme).text.primary,
            },
            '& .MuiListItemIcon-root': {
              color: resolvePalette(theme).text.primary,
            },
          },
        }),
      },
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: resolvePalette(theme).primary.main,
          cursor: 'pointer',
          '&:hover, &:active, &:focus': {
            color: resolvePalette(theme).primary.main,
          },
        }),
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: resolvePalette(theme).text.primary,
          '&.MuiAlert-colorSuccess': {
            backgroundColor: resolvePalette(theme).success.light,
          },
          '&.MuiAlert-colorError': {
            backgroundColor: resolvePalette(theme).error.light,
          },
          '&.MuiAlert-colorWarning': {
            backgroundColor: resolvePalette(theme).warning.light,
          },
          '&.MuiAlert-colorInfo': {
            backgroundColor: resolvePalette(theme).info.light,
          },
        }),
        icon: ({ theme }) => ({
          color: `${resolvePalette(theme).text.primary} !important`,
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'large',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: buttonRadius,
          textTransform: 'none',
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.hover,
          ...interactionStates.pressed,
          ...interactionStates.focus,
          variants: [
            {
              props: { size: 'large' },
              style: { fontSize: pxToRem(16) },
            },
          ],
        },
        // Contained button (PrimaryButton) styling
        contained: ({ theme }) => ({
          backgroundColor: resolvePalette(theme).secondary.main,
          color: common.white,
          ...theme.applyStyles('dark', {
            color: common.black,
          }),
        }),
        // Outlined button (SecondaryButton) styling
        outlined: ({ theme }) => ({
          borderColor: resolvePalette(theme).secondary.main,
          color: resolvePalette(theme).secondary.main,
          '&:hover, &:active': {
            backgroundColor: resolvePalette(theme).secondary.main,
            color: common.white,
            ...theme.applyStyles('dark', {
              color: common.black,
            }),
          },
        }),
        // Text button styling
        text: ({ theme }) => ({
          color: resolvePalette(theme).secondary.main,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.hover,
          ...interactionStates.pressed,
          ...interactionStates.focus,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.hover,
          ...interactionStates.pressed,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'all 0.1s ease-in-out',
          ...interactionStates.pressed,
          ...interactionStates.focus,
          '&.MuiChip-outlined': {
            backgroundColor: resolvePalette(theme).background.default,
            '&:hover': {
              backgroundColor: resolvePalette(theme).background.default,
              boxShadow: `inset 0 0 0 999px ${resolvePalette(theme).action.hover}`,
            },
          },
        }),
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          color: resolvePalette(theme).action.active,
          ...interactionStates.hover,
          ...interactionStates.pressed,
        }),
      },
    },
    MuiRadio: {
      defaultProps: {
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          transition: 'all 0.1s ease-in-out',
          color: resolvePalette(theme).action.active,
          ...interactionStates.hover,
          ...interactionStates.pressed,
        }),
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: resolvePalette(theme).action.active,
          '&.Mui-completed': {
            color: resolvePalette(theme).success.main,
          },
          '&.Mui-active': {
            color: resolvePalette(theme).primary.main,
          },
        }),
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          '& .MuiBottomNavigationAction-root': {
            transition: 'all 0.1s ease-in-out',
            ...interactionStates.hover,
            ...interactionStates.pressed,
          },
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        variant: 'scrollable',
        allowScrollButtonsMobile: true,
      },
      variants: [
        {
          props: { variant: 'scrollable' },
          style: {
            position: 'relative',
            '& .MuiTabs-scrollButtons': {
              position: 'absolute',
              height: '48px',
              zIndex: 1,
              opacity: 1,
              transition: 'opacity 0s linear 0.3s',
              backgroundColor: (theme.vars ?? theme).palette.background.default,
              '&:first-of-type': {
                left: 0,
              },
              '&:last-of-type': {
                right: 0,
              },
            },
            '& .MuiTabs-scrollButtons.Mui-disabled': {
              opacity: 0,
              transition: 'opacity 0s linear 0s',
            },
          },
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: `
          @font-face {
            font-family: 'Malarenergi Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 300;
            src: url('${baseUrl}/fonts/MalarenergiSans-Light.woff2') format('woff2');
          }
          @font-face {
            font-family: 'Malarenergi Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 400;
            src: url('${baseUrl}/fonts/MalarenergiSans-Regular.woff2') format('woff2');
          }
          @font-face {
            font-family: 'Malarenergi Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 700;
            src: url('${baseUrl}/fonts/MalarenergiSans-Bold.woff2') format('woff2');
          }
          @font-face {
            font-family: 'Malarenergi Sans';
            font-style: normal;
            font-display: swap;
            font-weight: 800;
            src: url('${baseUrl}/fonts/MalarenergiSans-ExtraBold.woff2') format('woff2');
          }
          @font-face {
            font-family: 'Malarenergi Sans';
            font-style: italic;
            font-display: swap;
            font-weight: 400;
            src: url('${baseUrl}/fonts/MalarenergiSans-Italic.woff2') format('woff2');
          }
          .vngage-banner {
            position: fixed !important;
            z-index: 1000 !important;
          }
          input:-webkit-autofill,
          input:-webkit-autofill:hover, 
          input:-webkit-autofill:focus,
          input:-webkit-autofill:active {
            -webkit-text-fill-color: ${typographyTextPrimary} !important;
          }
          a {
            color: inherit;
            text-decoration: none;
          }
          button:focus-visible,
          a:focus-visible,
          .Mui-focusVisible,
          .MuiCheckbox-root:not(label .MuiCheckbox-root):has(input:focus-visible),
          .MuiMenuItem-root:focus-visible,
          .MuiTab-root:focus-visible,
          .MuiSelect-select:focus-visible,
          .MuiTablePagination-select:has(.MuiSelect-select:focus-visible),
          .MuiListItemButton-root:focus-visible,
          .heatmapContainer:has(.heatmapTable:focus-visible),
          .MuiBreadcrumbs-root .MuiLink-root:focus-visible,
          .MuiBreadcrumbs-root .MuiTypography-root:focus-visible {
            outline-style: dashed;
            outline-width: 2px;
            outline-offset: 2px;
            outline-color: ${focusOutlineColor};
          }
          /* Text fields always match :focus-visible on click (browser
             heuristic), so scope their dashed outline to actual keyboard
             navigation via the .keyboard-nav class set in ThemeProvider.
             .MuiOutlinedInput-input is excluded here since that variant
             gets its dashed indicator from its notchedOutline below —
             otherwise both would draw at once (inner + outer ring). */
          html.keyboard-nav input:focus-visible:not(.MuiOutlinedInput-input),
          html.keyboard-nav .MuiFormControlLabel-root:has(input:focus-visible) {
            outline-style: dashed;
            outline-width: 2px;
            outline-offset: 2px;
            outline-color: ${focusOutlineColor};
          }
          /* Outlined text fields already draw their own rounded border via
             .MuiOutlinedInput-notchedOutline, which MUI thickens/solidifies
             on focus. Adding an extra outline on top of that produced a
             double, squared-off ring. Making that existing border dashed
             instead avoids the double outline and follows the field's
             rounded corners. */
          html.keyboard-nav .MuiOutlinedInput-root:has(:focus-visible) .MuiOutlinedInput-notchedOutline {
            border-style: dashed;
            border-color: ${focusOutlineColor};
          }
          nav .MuiListItemButton-root:focus-visible {
            background-color: transparent;
          }
          nav .MuiListItemButton-root:focus-visible,
          .MuiTab-root:focus-visible {
            outline-offset: -2px;
          }
    
          .MuiSnackbar-root .MuiSnackbarContent-root .MuiIconButton-root:focus-visible{
            outline-color: ${snackbarFocusOutlineColor};
          }
    
          .MuiMenuItem-root:focus-visible,
          .MuiTab-root:focus-visible,
          .MuiPickersCalendarHeader-switchViewButton:focus-visible,
          a.MuiListItemButton-root:focus-visible,
          .MuiBreadcrumbs-root .MuiLink-root:focus-visible,
          .MuiBreadcrumbs-root .MuiTypography-root:focus-visible{
          }
            .heatmapTable:focus-visible{
              outline: none;
              border: none;
            }
          `,
    },
  };

  return theme;
};

export const darkModeColors = createTheme(true).palette;
export const lightModeColors = createTheme(false).palette;

const serializeStyleSheet = (node: Record<string, unknown>): string => {
  let css = '';
  for (const key of Object.keys(node)) {
    const value = node[key];
    if (value && typeof value === 'object') {
      css += `${key}{${serializeStyleSheet(value as Record<string, unknown>)}}`;
    } else if (value != null) {
      const prop = key.startsWith('--')
        ? key
        : key.replace(/[A-Z]/g, (match: string) => `-${match.toLowerCase()}`);
      css += `${prop}:${value};`;
    }
  }
  return css;
};

export const getThemeCssVariables = (baseUrl = ''): string => {
  const theme = createTheme(false, baseUrl, true) as Theme & {
    generateStyleSheets?: () => Record<string, unknown>[];
  };
  const sheets = theme.generateStyleSheets?.() ?? [];
  return sheets.map((sheet) => serializeStyleSheet(sheet)).join('');
};
